/**
 * 암호화 서비스 - Web Crypto API 기반
 * 
 * webCryptoService를 기반으로 한 간소화된 암호화 서비스
 * 중복 코드를 제거하고 import 기반으로 재구성
 */

import { 
  encryptData, 
  decryptData, 
  WebCryptoInfo 
} from './webCryptoService.js';

// 핵심 암호화 함수들을 webCryptoService에서 가져옴
export { encryptData, decryptData };

/**
 * 암호화 데이터 검증
 * @param {Object} encryptedData - 검증할 암호화 데이터
 * @returns {Object} 검증 결과
 */
export function validateEncryptedData(encryptedData) {
  const result = {
    isValid: false,
    issues: [],
    standard: null,
    recommendations: []
  };

  // 필수 필드 검사
  const requiredFields = ['version', 'ciphertext', 'iv', 'salt'];
  for (const field of requiredFields) {
    if (!encryptedData[field]) {
      result.issues.push(`필수 필드 누락: ${field}`);
    }
  }

  // 표준 검증
  if (encryptedData.standard === 'WebCrypto-2025') {
    result.standard = 'WebCrypto-2025';
  } else if (encryptedData.version) {
    result.standard = 'Legacy';
    result.recommendations.push('Web Crypto API 표준으로 마이그레이션 권장');
  } else {
    result.standard = 'Unknown';
    result.issues.push('알 수 없는 암호화 형식');
  }

  result.isValid = result.issues.length === 0;
  return result;
}

/**
 * 암호화 서비스 정보 (webCryptoService에서 가져옴)
 */
export const EncryptionInfo = {
  ...WebCryptoInfo,
  description: 'Web Crypto API 기반 암호화 서비스'
};

/**
 * 기존 API와의 호환성을 위한 래퍼 함수들
 */
export const encrypt = async (text, userUid) => {
  // 입력 검증 강화
  if (typeof text !== 'string' || typeof userUid !== 'string') {
    throw new TypeError('text와 userUid는 문자열이어야 합니다');
  }
  
  if (text.length === 0) {
    throw new Error('빈 문자열은 암호화할 수 없습니다');
  }
  
  if (userUid.length < 10) {
    throw new Error('userUid는 최소 10자 이상이어야 합니다');
  }

  const result = await encryptData(text, userUid);
  return JSON.stringify(result);
};

export const decrypt = async (encryptedText, userUid) => {
  // 입력 검증 강화  
  if (typeof encryptedText !== 'string' || typeof userUid !== 'string') {
    throw new TypeError('encryptedText와 userUid는 문자열이어야 합니다');
  }
  
  if (encryptedText.length === 0) {
    throw new Error('빈 암호화 문자열은 복호화할 수 없습니다');
  }
  
  try {
    // JSON 형식인지 확인 (새로운 Web Crypto 형식)
    const encryptedData = JSON.parse(encryptedText);
    return await decryptData(encryptedData, userUid);
  } catch (parseError) {
    // JSON 파싱 실패시 레거시 데이터로 간주 - 복호화 시도하지 않음
    console.warn('⚠️ 레거시 암호화 데이터 감지됨. Web Crypto API 형식이 아닙니다.');
    console.info('💡 이 패스워드를 다시 저장하면 최신 암호화 방식으로 업데이트됩니다.');
    throw new Error('LEGACY_DATA_DETECTED');
  }
};
